import { observer } from 'mobx-react-lite';
import { Button, Card, Col, Row } from 'antd';
import React, { useMemo } from 'react';
import configurationStore from 'common/stores/configuration/configuration.store';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import { gatesTableInitialColumns } from '../community-information.config';
import TableBase from 'common/components/table/table-base';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import '../community-information.scss';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import AddEditGateModal from './edit-gate-modal/edit-gate-modal';
import { IGateConfiguration, ISystemConfigurationRead } from 'common/services/configuration-service/configuration.service.types';
import { PlusOutlined } from '@ant-design/icons';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import configurationService from 'common/services/configuration-service/configuration.service';
import { trackPromise } from 'react-promise-tracker';
import { notification } from 'common/utils/notification';

export const GatesConfiguration = observer(() => {
  const data = useMemo(() => {
    return configurationStore.configuration?.gates.map((i) => ({ ...i, id: i.name }));
  }, [configurationStore.configuration]);

  const tableControls = useTableControls({
    data: data,
    initialColumns: gatesTableInitialColumns,
    clientSide: true,
  });

  const addGateModal = useModal({
    onClose: (isSuccessSaved?: boolean) => {
      if (isSuccessSaved) {
        configurationStore.requestConfiguration();
      }
    },
  });

  const editGateModal = useModal({
    onBeforeShow: (setModalInitData) => {
      setModalInitData(tableControls.getSelectedRow());
    },
    onClose: (isSuccessSaved?: boolean, configuration?: ISystemConfigurationRead) => {
      if (isSuccessSaved) {
        configurationStore.setConfiguration(configuration!);
        const selectedRow = tableControls.getSelectedRow();
        const updatedGate = configuration?.gates.find((i) => i.name === selectedRow.name);
        tableControls.setSelectedRow(updatedGate);
      }
    },
  });

  const onDeleteClicked = async () => {
    const currentRow = tableControls.getSelectedRow() as IGateConfiguration;
    await trackPromise(configurationService.removeGateConfiguration(currentRow.name));
    configurationStore.requestConfiguration();
    notification.success({ message: 'Location successfully removed!', duration: 5 });
  };

  return (
    <Card className="eiq-card eiq-card-border eiq-configuration">
      <Row className="eiq-card-header">
        <Col span={24}>
          <h1 className="h1">Locations</h1>
        </Col>
      </Row>
      <TableContextProvider controls={tableControls}>
        <div className="gate-configuration-action-buttons">
          <Button icon={<PlusOutlined />} size="middle" onClick={addGateModal?.showModal}>
            Add
          </Button>
          <Button icon={<EditIcon />} size="middle" disabled={!tableControls.getSelectedRow()} onClick={editGateModal?.showModal}>
            Edit
          </Button>
          <Button
            type="primary"
            onClick={onDeleteClicked}
            className="button"
            disabled={!tableControls.getSelectedRow()}
            icon={<DeleteIcon stroke="#F6F8FA" />}>
            Delete
          </Button>
        </div>
        <TableBase />
      </TableContextProvider>
      <AddEditGateModal
        title="Edit"
        isEdit={true}
        initData={editGateModal?.initData}
        isOpen={editGateModal?.isOpenModal}
        onClose={editGateModal?.hideModal}
      />
      <AddEditGateModal
        title="Add"
        isEdit={false}
        initData={addGateModal?.initData}
        isOpen={addGateModal?.isOpenModal}
        onClose={addGateModal?.hideModal}
      />
    </Card>
  );
});
